import { useState, useEffect } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { BookOpen, LogOut, Heart, Users, Settings, Briefcase, ArrowRight, KeyRound, Megaphone, ShieldCheck } from 'lucide-react'
import { useAuth } from '../../shared/hooks/useAuth'
import { SystemDocs } from '../../shared/components/SystemDocs'
import './portal.css'

export function PortalPage() {
  const { signOut, role } = useAuth()
  const navigate = useNavigate()
  const [showSystemDocs, setShowSystemDocs] = useState(false)

  useEffect(() => {
    if (role === 'insurer') {
      navigate('/medical/claims', { replace: true })
    }
  }, [role, navigate])

  const handleSignOut = async () => {
    await signOut()
    window.location.href = '/'
  }

  if (role === 'ali') return <Navigate to="/groups/new-joiners" replace />

  return (
    <div className="elab-portal-home">
      <a className="elab-portal-skip-link" href="#elab-portal-workspaces">Skip to portals</a>
      <header className="elab-portal-header">
        <div className="elab-portal-header-inner">
          <img className="elab-portal-logo" src="/ELabLogo-DKpFPeDF.webp" alt="Employee Lab" width="178" height="64" />
          <nav className="elab-portal-utilities" aria-label="Account and help">
            <button type="button" onClick={() => setShowSystemDocs(true)} aria-label="Open documentation">
              <BookOpen aria-hidden="true" /><span>Documentation</span>
            </button>
            <Link to="/change-password" aria-label="Change password">
              <KeyRound aria-hidden="true" /><span>Change password</span>
            </Link>
            <button type="button" onClick={handleSignOut} className="elab-portal-signout" aria-label="Sign out">
              <LogOut aria-hidden="true" /><span>Sign out</span>
            </button>
          </nav>
        </div>
      </header>

      <main className="elab-portal-main" id="elab-portal-workspaces" tabIndex={-1}>
        <section className="elab-portal-welcome" aria-labelledby="elab-portal-title">
          <div className="elab-portal-welcome-copy">
            <h1 id="elab-portal-title" className="elab-portal-eyebrow"><span /> YOUR EMPLOYEE LAB WORKSPACE</h1>
          </div>
          <div className="elab-portal-welcome-art" aria-hidden="true">
            <div className="elab-portal-orbit elab-portal-orbit-outer" /><div className="elab-portal-orbit elab-portal-orbit-inner" />
            <div className="elab-portal-art-tile elab-portal-art-people"><Users /></div>
            <div className="elab-portal-art-tile elab-portal-art-heart"><Heart /></div>
            <span className="elab-portal-art-dot" />
          </div>
        </section>

        <section className="elab-portal-section" aria-labelledby="insurance-heading">
          <div className="elab-portal-section-heading">
            <h2 id="insurance-heading">Insurance portals</h2>
          </div>
          <div className="elab-portal-insurance-grid">
            <article className="elab-portal-card elab-portal-insurance-card">
              <div className="elab-portal-card-top"><div className="elab-portal-icon elab-portal-icon-orange"><Heart aria-hidden="true" /></div><span className="elab-portal-card-kicker">EMPLOYEE BENEFITS</span></div>
              <h3>NZ Railways</h3>
              <p>{role === 'employee_lab' ? 'Manage Life and Medical insurance for NZ Rail employees.' : 'Manage Medical insurance for NZ Rail employees.'}</p>
              <div className="elab-portal-card-actions">
                {role === 'employee_lab' && <Link to="/life" className="elab-portal-action elab-portal-action-light">Life <ArrowRight aria-hidden="true" /></Link>}
                <Link to="/medical" className="elab-portal-action elab-portal-action-light">Medical <ArrowRight aria-hidden="true" /></Link>
              </div>
            </article>
            <article className="elab-portal-card elab-portal-insurance-card elab-portal-groups-card">
              <div className="elab-portal-card-top"><div className="elab-portal-icon elab-portal-icon-orange"><Users aria-hidden="true" /></div><span className="elab-portal-card-kicker">GROUP SCHEMES</span></div>
              <h3>Group Insurance</h3>
              <p>Manage group schemes, companies and members in one place.</p>
              <div className="elab-portal-card-actions"><Link to="/groups" className="elab-portal-action elab-portal-action-dark">Open Group Insurance <ArrowRight aria-hidden="true" /></Link></div>
            </article>
          </div>
        </section>

        <section className="elab-portal-section" aria-labelledby="tools-heading">
          <div className="elab-portal-section-heading"><h2 id="tools-heading">Everyday tools</h2></div>
          <div className="elab-portal-tools-grid">
            <Link to="/pulse" className="elab-portal-card elab-portal-tool-card">
              <div className="elab-portal-tool-top"><div className="elab-portal-icon elab-portal-icon-neutral"><Settings aria-hidden="true" /></div><ArrowRight className="elab-portal-tool-arrow" aria-hidden="true" /></div>
              <h3>Pulse Admin</h3><p>Administrative tools and system management for Pulse.</p><span className="elab-portal-tool-link">Open Pulse Admin</span>
            </Link>
            <Link to="/general-admin" className="elab-portal-card elab-portal-tool-card">
              <div className="elab-portal-tool-top"><div className="elab-portal-icon elab-portal-icon-neutral"><Briefcase aria-hidden="true" /></div><ArrowRight className="elab-portal-tool-arrow" aria-hidden="true" /></div>
              <h3>General Admin</h3><p>Weekly reporting and meeting management for Employee Lab.</p><span className="elab-portal-tool-link">Open General Admin</span>
            </Link>
            {role === 'employee_lab' && <Link to="/marketing" className="elab-portal-card elab-portal-tool-card">
              <div className="elab-portal-tool-top"><div className="elab-portal-icon elab-portal-icon-neutral"><Megaphone aria-hidden="true" /></div><ArrowRight className="elab-portal-tool-arrow" aria-hidden="true" /></div>
              <h3>Marketing</h3><p>Create newsletters and manage Employee Lab marketing content.</p><span className="elab-portal-tool-link">Open Marketing</span>
            </Link>}
          </div>
        </section>
        <footer className="elab-portal-footer"><span><ShieldCheck aria-hidden="true" /> Employee Lab workspace</span><button type="button" onClick={() => setShowSystemDocs(true)}>Need a hand? <span>Read the documentation</span><ArrowRight aria-hidden="true" /></button></footer>
      </main>
      <SystemDocs isOpen={showSystemDocs} onClose={() => setShowSystemDocs(false)} />
    </div>
  )
}
